import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPolicies,
  exportPolicies,
  deleteAdmin,
  updatePolicy,
} from "../../utils/backend";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CInputGroup,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { getIn } from "formik";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

// Car / Motorcycle / Company bucket for a motor quote, shown next to the Insurance Type.
// A business-registered owner is "Company"; otherwise it's the body type from veh_type.
const vehicleCategory = (motor) => {
  if (!motor) return "";
  if ((motor.id_type || "").toLowerCase() === "business") return "Company";
  const vt = (motor.veh_type || "").toLowerCase();
  if (vt === "motorcycle") return "Motorcycle";
  if (vt === "car") return "Car";
  return "";
};

const InsurancePolicies = () => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const [insurerNameFilter, setInsurerNameFilter] = useState("");
  const [insurancePlanFilter, setInsurancePlanFilter] = useState("");
  const [orderNumberFilter, setOrderNumberFilter] = useState("");
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState("");
  const [paymentOrderIdFilter, setPaymentOrderIdFilter] = useState("");
  const [regNoFilter, setRegNoFilter] = useState("");
  const [idNoFilter, setIdNoFilter] = useState("");
  const [mobileNumberFilter, setMobileNumberFilter] = useState("");
  const [idFilter, setIdFilter] = useState(""); // (WIP)
  const [emailFilter, setEmailFilter] = useState(""); // (WIP)
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState(""); // (WIP)
  const [createdAtToFilter, setCreatedAtToFilter] = useState(""); // (WIP)
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setAdmins((prevAdmins) => [...prevAdmins]); // Trigger re-render to apply styles
  };

  const sortedAdmins = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return admins;
    return [...admins].sort((a, b) => {
      const getValue = (admin, key) => {
        if (key === "full_name") {
          return (
            admin?.order_details?.full_name ||
            admin?.foreign_worker_insurance?.enquirer_name ||
            ""
          ).toLowerCase();
        }
        if (key === "insurer_name") {
          return (admin?.order_data?.insurer_name || "").toLowerCase();
        }
        if (key === "order_number") {
          return (admin?.order_data?.order_number || "").toLowerCase();
        }
        return typeof admin[key] === "string"
          ? admin[key].toLowerCase()
          : admin[key];
      };

      const aValue = getValue(a, sortConfig.key);
      const bValue = getValue(b, sortConfig.key);

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [admins, sortConfig]);

  const hasPermission = (permissionGroup, permissionName) => {
    let permissions;
    try {
      permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      permissions = [];
    }

    return permissions.some(
      (perm) =>
        perm.permission_group === permissionGroup &&
        perm.permission_name === permissionName
    );
  };

  useEffect(() => {
    // Load filters and pagination state from localStorage on page load
    const savedState = JSON.parse(
      localStorage.getItem("insurancePoliciesState")
    );
    if (savedState) {
      setIdFilter(savedState.idFilter || ""); // (WIP)
      setOrderNumberFilter(savedState.orderNumberFilter || "");
      setPaymentOrderIdFilter(savedState.paymentOrderIdFilter || "");
      setRegNoFilter(savedState.regNoFilter || "");
      setIdNoFilter(savedState.idNoFilter || "");
      setMobileNumberFilter(savedState.mobileNumberFilter || "");
      setInsurerNameFilter(savedState.insurerNameFilter || "");
      setFullName(savedState.fullName || "");
      setInsuranceTypeFilter(savedState.insuranceTypeFilter || "");
      setEmailFilter(savedState.emailFilter || ""); // (WIP)
      setInsurancePlanFilter(savedState.insurancePlanFilter || "");
      setStatusFilter(savedState.statusFilter || "");
      setCreatedAtFromFilter(savedState.createdAtFromFilter || ""); // (WIP)
      setCreatedAtToFilter(savedState.createdAtToFilter || ""); // (WIP)
      setPerPage(savedState.perPage || 10);

      // Fetch data based on the saved state
      const filters = {
        id: savedState.idFilter || "", // (WIP)
        order_number: savedState.orderNumberFilter || "",
        payment_order_id: savedState.paymentOrderIdFilter || "",
        reg_no: savedState.regNoFilter || "",
        id_no: savedState.idNoFilter || "",
        mobile_number: savedState.mobileNumberFilter || "",
        insurer_name: savedState.insurerNameFilter || "",
        full_name: savedState.fullName || "",
        insurance_type: savedState.insuranceTypeFilter || "",
        email: savedState.emailFilter || "", // (WIP)
        insurance_plan: savedState.insurancePlanFilter || "",
        status: savedState.statusFilter || "",
        created_at_from: savedState.createdAtFromFilter || "", // (WIP)
        created_at_to: savedState.createdAtToFilter || "", // (WIP)
        start_date: savedState.createdAtFromFilter || "",
        end_date: savedState.createdAtToFilter || "",
      };
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // policies and nothing on screen explained why.
      const payload = {
        filters: filters,
        page: 1,
        perPage: savedState.perPage || 10,
      };

      fetchData(payload);
    } else {
      fetchData();
    }
  }, []);

  const saveStateToLocalStorage = () => {
    const existingState =
      JSON.parse(localStorage.getItem("insurancePoliciesState")) || {};
    // Evict keys written by older builds; the merge below would otherwise keep a
    // stale currentPage alive in every admin's browser indefinitely.
    delete existingState.currentPage;
    delete existingState.startDate;
    delete existingState.endDate;
    const state = {
      idFilter, // (WIP)
      orderNumberFilter,
      paymentOrderIdFilter,
      regNoFilter,
      idNoFilter,
      mobileNumberFilter,
      insurerNameFilter,
      fullName,
      insuranceTypeFilter,
      emailFilter, // (WIP)
      insurancePlanFilter,
      statusFilter,
      createdAtFromFilter, // (WIP)
      createdAtToFilter, // (WIP)
    };
    localStorage.setItem(
      "insurancePoliciesState",
      JSON.stringify({ ...existingState, ...state })
    );
  };

  const fetchData = async (filters = { perPage: perPage }) => {
    //setIsLoading(true);
    try {
      const response = await getPolicies(filters);
      setAdmins(response.data.data);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAdmins([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    const filters = {
      id: idFilter, // (WIP)
      order_number: orderNumberFilter,
      payment_order_id: paymentOrderIdFilter,
      reg_no: regNoFilter,
      id_no: idNoFilter,
      mobile_number: mobileNumberFilter,
      insurer_name: insurerNameFilter,
      full_name: fullName,
      insurance_type: insuranceTypeFilter,
      email: emailFilter, // (WIP)
      insurance_plan: insurancePlanFilter,
      status: statusFilter,
      start_date: createdAtFromFilter,
      end_date: createdAtToFilter,
    };
    const payload = {
      filters: filters,
      page: 1,
      perPage: perPage,
    };
    saveStateToLocalStorage(); // Save state before fetching data
    await fetchData(payload);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    idFilter,
    orderNumberFilter,
    paymentOrderIdFilter,
    regNoFilter,
    idNoFilter,
    mobileNumberFilter,
    insurerNameFilter,
    username,
    fullName,
    emailFilter,
    insurancePlanFilter,
    insuranceTypeFilter,
    statusFilter,
    createdAtFromFilter,
    createdAtToFilter,
    perPage,
  ]);

  const handleReset = () => {
    setIdFilter(""); // (WIP)
    setOrderNumberFilter("");
    setPaymentOrderIdFilter("");
    setRegNoFilter("");
    setIdNoFilter("");
    setMobileNumberFilter("");
    setInsurerNameFilter("");
    setFullName("");
    setInsuranceTypeFilter("");
    setEmailFilter(""); // (WIP)
    setInsurancePlanFilter("");
    setStatusFilter("");
    setCreatedAtFromFilter(""); // (WIP)
    setCreatedAtToFilter(""); // (WIP)
    setCurrentPage(1);

    const existingState =
      JSON.parse(localStorage.getItem("insurancePoliciesState")) || {};
    localStorage.setItem(
      "insurancePoliciesState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const filters = {
        id: idFilter,
        order_number: orderNumberFilter,
        payment_order_id: paymentOrderIdFilter,
        reg_no: regNoFilter,
        id_no: idNoFilter,
        mobile_number: mobileNumberFilter,
        insurer_name: insurerNameFilter,
        full_name: fullName,
        insurance_type: insuranceTypeFilter,
        email: emailFilter,
        insurance_plan: insurancePlanFilter,
        status: statusFilter,
        start_date: createdAtFromFilter,
        end_date: createdAtToFilter,
      };
      const blob = await exportPolicies({ filters });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Insurance_Policies_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = async (page) => {
    const filters = {
      id: idFilter, // (WIP)
      order_number: orderNumberFilter,
      payment_order_id: paymentOrderIdFilter,
      reg_no: regNoFilter,
      id_no: idNoFilter,
      mobile_number: mobileNumberFilter,
      insurer_name: insurerNameFilter,
      full_name: fullName,
      insurance_type: insuranceTypeFilter,
      email: emailFilter, // (WIP)
      insurance_plan: insurancePlanFilter,
      status: statusFilter,
      created_at_from: createdAtFromFilter, // (WIP)
      created_at_to: createdAtToFilter, // (WIP)
      start_date: createdAtFromFilter,
      end_date: createdAtToFilter,
    };
    const payload = {
      filters: filters,
      page: page,
      perPage: perPage,
    };
    await fetchData(payload);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "insurancePoliciesState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("insurancePoliciesState")),
        perPage: newPerPage,
      })
    );
    const filters = {
      id: idFilter, // (WIP)
      order_number: orderNumberFilter,
      payment_order_id: paymentOrderIdFilter,
      reg_no: regNoFilter,
      id_no: idNoFilter,
      mobile_number: mobileNumberFilter,
      insurer_name: insurerNameFilter,
      full_name: fullName,
      insurance_type: insuranceTypeFilter,
      email: emailFilter, // (WIP)
      insurance_plan: insurancePlanFilter,
      status: statusFilter,
      created_at_from: createdAtFromFilter, // (WIP)
      created_at_to: createdAtToFilter, // (WIP)
      start_date: createdAtFromFilter,
      end_date: createdAtToFilter,
    };
    const payload = {
      filters: filters,
      page: 1,
      perPage: newPerPage,
    };
    await fetchData(payload);
  };

  const handleStatusChange = async (userId, newStatus) => {
    const updatedStatus =
      newStatus.charAt(0).toUpperCase() + newStatus.slice(1);

    // Find the original user and store its status
    const originalUser = admins.find((user) => user.id === userId);
    const originalStatus = originalUser ? originalUser.status : null;
    // If the new status is the same as the current status, do nothing
    if (originalStatus === updatedStatus) {
      return;
    }

    // Optimistically update the UI
    setAdmins((prevLogs) =>
      prevLogs.map((user) =>
        user.id === userId ? { ...user, status: updatedStatus } : user
      )
    );

    try {
      const response = await updatePolicy({
        id: userId,
        status: newStatus,
      });
      if (response.success) {
        toast.success("Policy status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");

      // Revert the status if the update fails
      setAdmins((prevLogs) =>
        prevLogs.map((user) =>
          user.id === userId ? { ...user, status: originalStatus } : user
        )
      );
    }
  };

  const insuranceTypeMapping = {
    1: "Foreign Worker",
    2: "Term Life",
    3: "Health Insurance",
    4: "Personal Accident",
    5: "Motor",
    6: "Offshore Insurance",
    7: "Group Insurance",
    8: "Property Insurance",
  };

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  // Surfaced next to Reset so an admin can never be silently filtered down to a
  // subset (a date range set in the far-right column used to scroll out of view).
  const activeFilterCount = [
    idFilter,
    orderNumberFilter,
    paymentOrderIdFilter,
    regNoFilter,
    idNoFilter,
    mobileNumberFilter,
    insurerNameFilter,
    fullName,
    insuranceTypeFilter,
    emailFilter,
    insurancePlanFilter,
    statusFilter,
    createdAtFromFilter,
    createdAtToFilter,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      {/* Keep the header (search row + column titles) pinned to the top of the
          height-bounded .policies-scroll viewport while rows scroll underneath. */}
      <style>{`
        .policies-scroll thead {
          position: sticky;
          top: 0;
          z-index: 2;
        }
        .policies-scroll thead th,
        .policies-scroll thead td {
          background-color: #fff;
        }
      `}</style>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  <CButton
                    color="success"
                    className="text-white me-2"
                    type="button"
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <CSpinner size="sm" />
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-excel me-2"></i>
                        Download Excel
                      </>
                    )}
                  </CButton>
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              {/* Height-bounded scroll viewport: keeps the horizontal scrollbar
                  pinned to the bottom of this region (and the pagination bar below
                  it) always in view, so admins don't have to scroll the whole page
                  down past 200+ rows to scroll left/right. */}
              <div
                className="policies-scroll"
                style={{
                  maxHeight: "calc(100vh - 320px)",
                  overflow: "auto",
                }}
              >
                <CTable borderless striped>
                  <CTableHead>
                    <CTableRow>
                      {/* No. */}
                      <CTableDataCell></CTableDataCell>
                      {/* Actions */}
                      <CTableDataCell></CTableDataCell>
                      {/* Transaction Date */}
                      <CTableDataCell></CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={orderNumberFilter}
                          onChange={(e) => setOrderNumberFilter(e.target.value)}
                        />
                      </CTableDataCell>
                      {/* Order ID (Revenue Monster) — matches the ANS- value in the payment remark */}
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={paymentOrderIdFilter}
                          onChange={(e) =>
                            setPaymentOrderIdFilter(e.target.value)
                          }
                        />
                      </CTableDataCell>
                      {/* Vehicle Number */}
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={regNoFilter}
                          onChange={(e) => setRegNoFilter(e.target.value)}
                        />
                      </CTableDataCell>
                      {/* ID Number */}
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={idNoFilter}
                          onChange={(e) => setIdNoFilter(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={emailFilter}
                          onChange={(e) => setEmailFilter(e.target.value)}
                        />
                      </CTableDataCell>
                      {/* Contact Number */}
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={mobileNumberFilter}
                          onChange={(e) => setMobileNumberFilter(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormSelect
                          id="insurance_type"
                          value={insuranceTypeFilter}
                          onChange={(e) =>
                            setInsuranceTypeFilter(e.target.value)
                          }
                        >
                          <option value="">Select Insurance Type</option>
                          {Object.entries(insuranceTypeMapping).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            )
                          )}
                        </CFormSelect>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={insurancePlanFilter}
                          onChange={(e) =>
                            setInsurancePlanFilter(e.target.value)
                          }
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          type="text"
                          placeholder="Search"
                          value={insurerNameFilter}
                          onChange={(e) => setInsurerNameFilter(e.target.value)}
                        />
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormSelect
                          id="status"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">Select Status</option>
                          <option value="Active">Active</option>
                          <option value="Pending">Pending</option>
                          <option value="Expired">Expired</option>
                          <option value="Pending Policy">Pending Policy</option>
                          <option value="Upcoming Renewals">
                            Upcoming Renewals
                          </option>
                        </CFormSelect>
                      </CTableDataCell>
                      {/* Created At (date range) */}
                      <CTableDataCell>
                        <CInputGroup style={{ minWidth: "250px" }}>
                          <CFormInput
                            type="date"
                            value={createdAtFromFilter}
                            onChange={(e) =>
                              setCreatedAtFromFilter(e.target.value)
                            }
                            title="Start Date"
                          />

                          <span className="input-group-text bg-light text-muted small">
                            to
                          </span>

                          <CFormInput
                            type="date"
                            value={createdAtToFilter}
                            onChange={(e) => setCreatedAtToFilter(e.target.value)}
                            title="End Date"
                          />
                        </CInputGroup>
                      </CTableDataCell>
                    </CTableRow>
                    <CTableRow>
                      <CTableHeaderCell
                        onClick={() => {}}
                        className={`hover-header ${
                          sortConfig.key === "id" ? "ctable-active-header" : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          minWidth: "60px",
                        }}
                      >
                        No.{" "}
                        {sortConfig.key === "id" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-numeric-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-numeric-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell>Actions</CTableHeaderCell>
                      <CTableHeaderCell style={{ minWidth: "160px" }}>
                        Transaction Date
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("order_number")}
                        className={`hover-header ${
                          sortConfig.key === "order_number"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer", minWidth: "100px" }}
                      >
                        Order Number{" "}
                        {sortConfig.key === "order_number" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell style={{ minWidth: "150px" }}>
                        Order ID (RM)
                      </CTableHeaderCell>
                      <CTableHeaderCell style={{ minWidth: "120px" }}>
                        Vehicle Number
                      </CTableHeaderCell>
                      <CTableHeaderCell style={{ minWidth: "130px" }}>
                        ID Number
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("full_name")}
                        className={`hover-header ${
                          sortConfig.key === "full_name"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        Full Name{" "}
                        {sortConfig.key === "full_name" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("email")}
                        className={`hover-header ${
                          sortConfig.key === "email"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        Email Address{" "}
                        {sortConfig.key === "email" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell style={{ minWidth: "130px" }}>
                        Contact Number
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("insurance_type")}
                        className={`hover-header ${
                          sortConfig.key === "insurance_type"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer", minWidth: "150px" }}
                      >
                        Insurance Type{" "}
                        {sortConfig.key === "insurance_type" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("insurance_plan")}
                        className={`hover-header ${
                          sortConfig.key === "insurance_plan"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        Insurance Plan{" "}
                        {sortConfig.key === "insurance_plan" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("insurer_name")}
                        className={`hover-header ${
                          sortConfig.key === "insurer_name"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        Insurer Name{" "}
                        {sortConfig.key === "insurer_name" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("status")}
                        className={`hover-header ${
                          sortConfig.key === "status"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        Policy Status{" "}
                        {sortConfig.key === "status" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-alpha-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-alpha-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                      <CTableHeaderCell
                        onClick={() => handleSort("created_at")}
                        className={`hover-header ${
                          sortConfig.key === "created_at"
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{ cursor: "pointer", width: "250px" }}
                      >
                        Created At{" "}
                        {sortConfig.key === "created_at" ? (
                          sortConfig.direction === "asc" ? (
                            <i className="bi bi-sort-numeric-down float-end"></i>
                          ) : (
                            <i className="bi bi-sort-numeric-up float-end"></i>
                          )
                        ) : (
                          <i className="bi bi-arrow-down-up float-end text-muted"></i>
                        )}
                      </CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {sortedAdmins.map((admin, index) => {
                      const insuranceType =
                        admin?.order_details?.insurance_type;
                      const subscription_plan_name =
                        admin?.order_data?.subscription_plan_name;
                      let personalInfo = {};

                      switch (insuranceType) {
                        case 1:
                          personalInfo = admin.foreign_worker_insurance || {};
                          break;
                        case 2:
                          personalInfo = admin.term_life_insurance || {};
                          break;
                        case 3:
                          personalInfo = admin.health_insurance || {};
                          break;
                        case 4:
                          personalInfo = admin.pa_insurance || {};
                          break;
                        case 5:
                          personalInfo = admin.motor_insurance || {};
                          break;
                        case 6:
                          personalInfo = admin.offshore_insurance || {};
                          break;
                        case 7:
                          personalInfo = admin.group_insurance || {};
                          break;
                        case 8:
                          personalInfo = admin.property_insurance || {};
                          break;
                        default:
                          personalInfo = {};
                      }

                      const { enquirer_name } = personalInfo;

                      const fullName =
                        admin?.order_details?.full_name || enquirer_name;

                      return (
                        <CTableRow key={admin.id}>
                          <CTableDataCell>
                            {index + 1 + perPage * (currentPage - 1)}
                          </CTableDataCell>
                          <CTableDataCell>
                            <div className="d-flex align-items-center gap-2">
                              {hasPermission("Policy Management", "View") && (
                                <CButton
                                  className="btn btn-sm btn-info text-white"
                                  title="Details"
                                  onClick={() =>
                                    navigate(
                                      `/insurance-policies/details/${admin.id}`
                                    )
                                  }
                                >
                                  <i className="bi bi-eye-fill"></i>
                                </CButton>
                              )}
                              {hasPermission("Policy Management", "Edit") && (
                                <CButton
                                  className="btn btn-sm btn-warning text-white"
                                  title="Edit"
                                  onClick={() =>
                                    navigate(
                                      `/insurance-policies/edit/${admin.id}`
                                    )
                                  }
                                >
                                  <i className="bi bi-pencil-fill"></i>
                                </CButton>
                              )}
                            </div>
                          </CTableDataCell>
                          <CTableDataCell>
                            {admin?.order_data?.transaction_date
                              ? formatDateTime(admin.order_data.transaction_date)
                              : "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {(admin.order_data &&
                              admin.order_data.order_number) ||
                              "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {(admin.order_data &&
                              admin.order_data.payment_order_id) ||
                              "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {(admin.motor_insurance &&
                              admin.motor_insurance.reg_no) ||
                              "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {(admin.motor_insurance &&
                              admin.motor_insurance.id_no) ||
                              admin?.order_details?.documentID ||
                              "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {setUppercase(fullName) || "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {admin?.order_details?.email_address ||
                              admin.email ||
                              "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {admin?.order_details?.mobile_number || "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {admin.order_details &&
                            insuranceTypeMapping[
                              admin.order_details.insurance_type
                            ]
                              ? `${setUppercase(
                                  insuranceTypeMapping[
                                    admin.order_details.insurance_type
                                  ]
                                )}${
                                  admin.order_details.insurance_type === 5 &&
                                  vehicleCategory(admin.motor_insurance)
                                    ? ` (${vehicleCategory(
                                        admin.motor_insurance
                                      )})`
                                    : ""
                                }`
                              : "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {subscription_plan_name &&
                            subscription_plan_name.trim() !== ""
                              ? setUppercase(subscription_plan_name)
                              : admin.order_data?.insurance_plan &&
                                admin.order_data.insurance_plan.trim() !== ""
                              ? setUppercase(admin.order_data.insurance_plan)
                              : personalInfo.plan_name &&
                                personalInfo.plan_name.trim() !== ""
                              ? setUppercase(personalInfo.plan_name)
                              : "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            {(admin.order_data &&
                              admin.order_data.insurer_name) ||
                              "N/A"}
                          </CTableDataCell>
                          <CTableDataCell>
                            <CDropdown>
                              <CDropdownToggle
                                color="secondary"
                                variant="outline"
                                disabled={
                                  !hasPermission("Policy Management", "Edit")
                                }
                                style={{
                                  backgroundColor:
                                    admin.status === "Active"
                                      ? "#66BB6A"
                                      : "#8B0000",
                                  color: "#fff",
                                  border: "none",
                                  width: "160px",
                                  borderRadius: "20px",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                {admin.status
                                  ? admin.status.toUpperCase()
                                  : "N/A"}
                              </CDropdownToggle>
                              <CDropdownMenu>
                                <CDropdownItem
                                  onClick={() =>
                                    handleStatusChange(admin.id, "Active")
                                  }
                                >
                                  Active
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() =>
                                    handleStatusChange(admin.id, "Pending")
                                  }
                                >
                                  Pending
                                </CDropdownItem>

                                <CDropdownItem
                                  onClick={() =>
                                    handleStatusChange(admin.id, "Expired")
                                  }
                                >
                                  Expired
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() =>
                                    handleStatusChange(
                                      admin.id,
                                      "Pending Policy"
                                    )
                                  }
                                >
                                  Pending Policy
                                </CDropdownItem>
                                <CDropdownItem
                                  onClick={() =>
                                    handleStatusChange(
                                      admin.id,
                                      "Upcoming Renewals"
                                    )
                                  }
                                >
                                  Upcoming Renewals
                                </CDropdownItem>
                              </CDropdownMenu>
                            </CDropdown>
                          </CTableDataCell>
                          <CTableDataCell>
                            {admin.created_at
                              ? formatDateTime(admin.created_at)
                              : "N/A"}
                          </CTableDataCell>
                        </CTableRow>
                      );
                    })}
                  </CTableBody>
                </CTable>
              </div>
            </>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(
                      {
                        idFilter,
                        orderNumberFilter,
                        paymentOrderIdFilter,
                        regNoFilter,
                        idNoFilter,
                        mobileNumberFilter,
                        insurerNameFilter,
                        fullName,
                        insuranceTypeFilter,
                        emailFilter,
                        insurancePlanFilter,
                        statusFilter,
                        createdAtFromFilter,
                        createdAtToFilter,
                        perPage: perPage,
                      },
                      page
                    );
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  {/* ...existing pagination items... */}
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {admins.length} of {totalItems} policies
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(Number(e.target.value))}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default InsurancePolicies;
